/**
 * Real-time watcher for the per-vehicle `ownershipMismatch` flag.
 *
 * Listens on `vehicles/{plate}/ownershipMismatch` for the CURRENT user's
 * plate (whatever's in LocalState). When the value changes:
 *   - Updates `window.isOwnershipMismatched` so legacy search-block kicks
 *     in immediately (used by www/modals/search-vehicle.js + feed-cards.js).
 *   - Re-renders the license/ownership banners.
 *   - Emits `vehicle:changed` so the alert-status dot recomputes.
 *
 * Triggered by the admin-side DEV tool that toggles the flag — gives a
 * real-time preview of the mismatch state on the user's device with no
 * app reload required.
 *
 * Lifecycle:
 *   `init()` is called once from bootstrap. It re-subscribes when
 *   `vehicle:changed` fires (in case the plate itself changed via the
 *   replace flow).
 */

import { ref } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import * as bus from '../../core/eventBus.js';
import { createLogger } from '../../core/logger.js';
import { render as renderBanners } from './licenseBanner.view.js';

const log = createLogger('ownership-watcher');

/** @type {any} */
let currentRef = null;
/** @type {string | null} */
let watchedPlate = null;
let initialised = false;

function applyFlag(rawValue) {
  const flagged = String(rawValue ?? '0') === '1';
  /** @type {any} */ (window).isOwnershipMismatched = flagged;

  // Repaint the banners in any visible host. `licenseBanner.render` is
  // idempotent and cheap — picks up the flag from the same DB path on its
  // own, so we don't need to pass it in.
  for (const hostId of ['accountContent', 'myCarSubModalContent']) {
    if (document.getElementById(hostId)) {
      void renderBanners(hostId).catch((e) => log.warn('renderBanners:', e));
    }
  }

  // Bubble to the alert-status dot service.
  bus.emit('vehicle:changed', { plate: watchedPlate || '' });
}

function attach(plate) {
  if (watchedPlate === plate) return;
  detach();
  if (!plate) return;
  watchedPlate = plate;
  currentRef = ref(`vehicles/${plate}/ownershipMismatch`);
  currentRef.on('value', (snap) => {
    try { applyFlag(snap.val()); }
    catch (e) { log.warn('applyFlag failed:', e); }
  });
}

function detach() {
  if (currentRef) {
    try { currentRef.off(); } catch (_) { /* ignore */ }
  }
  currentRef = null;
  watchedPlate = null;
}

/**
 * Wire up the watcher. Idempotent — safe to call from multiple places.
 * Re-subscribes to the new plate whenever the user replaces their vehicle
 * OR completes auth (the initial bootstrap may run before LocalState.plate
 * is populated, in which case we need a second pass once auth is ready).
 */
export function init() {
  if (initialised) return;
  initialised = true;
  const reattach = () => {
    const next = normalizePlate(LocalState.plate ?? '');
    if (next !== watchedPlate) attach(next);
  };
  reattach();
  bus.on('auth:ready', reattach);
  bus.on('vehicle:changed', reattach);
  bus.on('auth:logged-out', detach);
}
